import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ── Types ──────────────────────────────────────────────────────────
export interface RiskKPI {
  contract_id: string;
  contract_name: string;
  cluster_id: string;
  cluster_name: string;
  periodo: string;
  prob_atraso: number | null;
  variacao_custo_pct: number | null;
  exposicao_financeira: number | null;
  score_risco: number | null;
  ranking_prioridade: number | null;
  total_ocorrencias: number;
  ocorrencias_criticas: number;
  taxa_reincidencia: number;
  tendencia: string | null;
  source: string;
  updated_at: string;
}

export interface MonteCarloResult {
  run_id: string;
  contract_id: string;
  contract_name: string;
  cluster_id: string;
  n_simulations: number;
  status: string;
  completed_at: string | null;
  resultado: Record<string, any> | null;
  melhor_prazo: number | null;
  melhor_custo: number | null;
  melhor_prob: number | null;
  provavel_prazo: number | null;
  provavel_custo: number | null;
  provavel_prob: number | null;
  pior_prazo: number | null;
  pior_custo: number | null;
  pior_prob: number | null;
}

export interface RiskIntelligenceData {
  kpis: RiskKPI[];
  monteCarlo: MonteCarloResult | null;
  summary: {
    avgScoreRisco: number;
    avgProbAtraso: number;
    totalExposicao: number;
    contratosRisco: number;
    tendenciaGeral: 'melhorando' | 'estavel' | 'piorando';
  } | null;
}

// ── Hook for a single contract ─────────────────────────────────────
export function useRiskIntelligenceContract(contractId: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState<RiskKPI[]>([]);
  const [monteCarlo, setMonteCarlo] = useState<MonteCarloResult | null>(null);
  const [mcRunning, setMcRunning] = useState(false);
  const hasFetched = useRef(false);

  const fetchData = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const [kpiRes, mcRes] = await Promise.all([
        supabase
          .from('vw_risk_kpis_contrato' as any)
          .select('*')
          .eq('contract_id', contractId)
          .order('periodo', { ascending: false })
          .limit(12),
        supabase
          .from('vw_risk_monte_carlo_latest' as any)
          .select('*')
          .eq('contract_id', contractId)
          .limit(1),
      ]);

      if (kpiRes.data) setKpis(kpiRes.data as unknown as RiskKPI[]);
      if (mcRes.data && mcRes.data.length > 0) {
        setMonteCarlo(mcRes.data[0] as unknown as MonteCarloResult);
      }
    } catch (err) {
      console.error('Error fetching risk intelligence:', err);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (!hasFetched.current && contractId) {
      hasFetched.current = true;
      fetchData();
    }
  }, [fetchData, contractId]);

  // Realtime subscription for auto-refresh
  useEffect(() => {
    if (!contractId) return;

    const channel = supabase
      .channel(`risk-contract-${contractId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'risk_monte_carlo_runs',
          filter: `contract_id=eq.${contractId}`,
        },
        (payload) => {
          const newRow = payload.new as any;
          if (newRow?.status === 'completed') {
            toast.info('Simulação Monte Carlo atualizada', {
              description: 'Novos resultados disponíveis.',
            });
            fetchData();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'emp_forecast_montecarlo',
          filter: `contrato_id=eq.${contractId}`,
        },
        () => {
          toast.info('Projeção atualizada automaticamente', {
            description: 'Os dados de Monte Carlo foram recalculados.',
          });
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contractId, fetchData]);

  // Trigger Monte Carlo simulation
  const runMonteCarlo = useCallback(async () => {
    if (!contractId || mcRunning) return;
    setMcRunning(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: run, error: runErr } = await supabase
        .from('risk_monte_carlo_runs')
        .insert({
          contract_id: contractId,
          triggered_by: user.id,
          tipo: 'manual',
          n_simulations: 10000,
          status: 'pending',
        })
        .select('id')
        .single();

      if (runErr) throw runErr;

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/risk-monte-carlo`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token ?? ''}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contract_id: contractId,
            run_id: run.id,
            n_simulations: 10000,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Monte Carlo failed');
      }

      toast.success('Simulação concluída', {
        description: 'Resultados Monte Carlo atualizados.',
      });
      await fetchData();
    } catch (err) {
      console.error('Monte Carlo error:', err);
      toast.error('Erro na simulação Monte Carlo');
    } finally {
      setMcRunning(false);
    }
  }, [contractId, mcRunning, fetchData]);

  return {
    loading,
    kpis,
    monteCarlo,
    mcRunning,
    runMonteCarlo,
    refresh: fetchData,
  };
}

// ── Hook for cluster-level consolidation ───────────────────────────
export function useRiskIntelligenceCluster(clusterId: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RiskIntelligenceData>({
    kpis: [],
    monteCarlo: null,
    summary: null,
  });
  const hasFetched = useRef(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('vw_risk_kpis_cluster' as any)
        .select('*')
        .order('score_risco', { ascending: false })
        .limit(200);

      if (clusterId) {
        query = query.eq('cluster_id', clusterId);
      }

      const { data: kpiData, error } = await query;
      if (error) throw error;

      const kpis = (kpiData || []) as unknown as RiskKPI[];

      const validScores = kpis.filter(k => k.score_risco !== null);
      const validProbs = kpis.filter(k => k.prob_atraso !== null);

      const avgScoreRisco = validScores.length > 0
        ? validScores.reduce((s, k) => s + (k.score_risco || 0), 0) / validScores.length
        : 0;
      const avgProbAtraso = validProbs.length > 0
        ? validProbs.reduce((s, k) => s + (k.prob_atraso || 0), 0) / validProbs.length
        : 0;
      const totalExposicao = kpis.reduce((s, k) => s + (k.exposicao_financeira || 0), 0);
      const contratosRisco = kpis.filter(k => (k.score_risco || 0) > 60).length;

      const melhorando = kpis.filter(k => k.tendencia === 'melhorando').length;
      const piorando = kpis.filter(k => k.tendencia === 'piorando').length;
      const tendenciaGeral: 'melhorando' | 'estavel' | 'piorando' =
        melhorando > piorando ? 'melhorando' : piorando > melhorando ? 'piorando' : 'estavel';

      let trendQuery = supabase
        .from('vw_risk_trend_history' as any)
        .select('*')
        .order('periodo', { ascending: false })
        .limit(500);

      if (clusterId) {
        trendQuery = trendQuery.eq('cluster_id', clusterId);
      }

      const { data: trendData } = await trendQuery;

      setData({
        kpis: trendData ? [...kpis, ...(trendData as unknown as RiskKPI[])] : kpis,
        monteCarlo: null,
        summary: {
          avgScoreRisco: Math.round(avgScoreRisco),
          avgProbAtraso: Math.round(avgProbAtraso),
          totalExposicao,
          contratosRisco,
          tendenciaGeral,
        },
      });
    } catch (err) {
      console.error('Error fetching cluster risk data:', err);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [fetchData]);

  // Realtime subscription for cluster-level updates
  useEffect(() => {
    const channel = supabase
      .channel('risk-cluster-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'risk_monte_carlo_runs',
        },
        (payload) => {
          const newRow = payload.new as any;
          if (newRow?.status === 'completed') {
            toast.info('Indicadores de risco atualizados', {
              description: 'Novos dados de simulação disponíveis.',
            });
            fetchData();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchData]);

  return { loading, ...data, refresh: fetchData };
}
